import { Component, ElementRef, Input, OnChanges, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { DialogService, DialogRef, DialogSettings, DialogCloseResult, WindowService, WindowSettings } from '@progress/kendo-angular-dialog';
import { EntityComponent } from '../../literals/entity-components.literal';
import { FormGroup } from '@angular/forms';
import { TabControl } from '../../ui/controls/tab-control.model';
import { UiColumn } from '../../ui/ui-column';
import { DetailService } from '@core/services/detail.service';
import { FeedbackMessageService } from '@shared/services/feedback-message.service';
import { TranslateService } from '@ngx-translate/core';
import { CommonModule, getLocaleCurrencyCode } from '@angular/common';
import { PagingAction, PagingOptions } from '../../interfaces/tab-option.interface';
import { EntityService } from '@shared/services/entity.service';
import { ToolbarItem } from '../../interfaces/tool-bar-item.interface';
import { faEdit, IconDefinition } from '@fortawesome/free-regular-svg-icons';
import { faX } from '@fortawesome/free-solid-svg-icons';
import { CardModule } from '@progress/kendo-angular-layout';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { INotificationService } from '../../services/inotification.service';

@Component({
    selector: 'app-tab',
    imports: [CardModule, ButtonModule, FontAwesomeModule, IndicatorsModule, CommonModule],
    providers: [EntityService, FeedbackMessageService, TranslateService, DialogService, WindowService, DetailService],
    templateUrl: './tab.component.html',
    styleUrls: ['./tab.component.css']
})
export class TabComponent implements OnInit, OnChanges {

  @Input() tab: TabControl;
  @Input() data: Array<any>;
  @Output() dataChange: EventEmitter<Array<any>> = new EventEmitter<Array<any>>();

  @ViewChild("tabTable", { static: true }) public table: ElementRef;

  pagingActions: PagingAction[] = [];
  pageSize: number;
  pages: number;
  pagedData: Array<any>;
  pagingOptions: PagingOptions | undefined;
  shouldPage: boolean;
  pageRange: string;
  total: number;

  showAddBtn: boolean = true;

  linkTarget: string = "_blank";
  icoEdit: IconDefinition = faEdit;
  icoDelete: IconDefinition = faX;

  constructor(private dialogService: DialogService,
              private windowService: WindowService,
              private detailService: DetailService<any>,
              private feedback: FeedbackMessageService,
              private translateService: TranslateService,
              private notificationService: INotificationService,
              private entityService: EntityService<any>) { }

  ngOnInit(): void {
    this.pagingOptions = this.tab.tabOptions.pagingOptions;
    this.pageSize = this.pagingOptions?.size ?? 10;
    this.pagedData = this.data ?? [];
    this.shouldPage = this.calculateShouldPage();
    this.showAddBtn = this.tab.tabOptions.showAddBtn == undefined || this.tab.tabOptions.showAddBtn == true;

    if (this.shouldPage && this.tab.tabOptions.isDataPreloaded) {
      this.pagedData = this.data.skip(0).take(this.pageSize)
      this.pages = Math.ceil(this.data.length / this.pageSize);

      for (var i = 0; i < this.pages; i++) {
        this.pagingActions.push({ index: i, text: (i + 1).toString() });
      }

      this.total = this.data.length;
      this.pageRange = "1 - " + (this.pageSize);
    } else {
      this.pagedData = this.data;
    }
  }

  calculateShouldPage() {
    return (
      this.pagingOptions != undefined && this.pagingOptions?.enable == true
      && this.data != undefined && this.data.length > this.pageSize
    );
  }

  ngOnChanges() {
    this.shouldPage = this.calculateShouldPage();

    if (this.shouldPage) {
      this.pagedData = this.data.skip(0).take(this.pageSize);
      this.pages = Math.ceil(this.data.length / this.pageSize);

      for (var i = 0; i < this.pages; i++) {
        this.pagingActions.push({ index: i, text: (i + 1).toString() });
      }

      this.total = this.data.length;
      this.pageRange = "1 - " + (this.pageSize);
    } else {
      this.pagedData = this.data;
    }
  }

  page(index: number) {
    var body = (this.table.nativeElement as HTMLTableElement).tBodies[0];
    while (body.rows.length > 0) {
      body.deleteRow(0);
    }

    var pageDataSets = this.data.chunk(this.pageSize);
    this.pagedData = pageDataSets[index];

    var start = (index * 10) + 1;
    var end = start + (this.pageSize - 1);

    if (end > this.total) {
      end = this.total;
    }

    this.pageRange = (start + " - " + end).toString();
  }

  getConvertedItemValue(value: any, format: string | undefined): string {
    if (value === true || value === "true" || value === "True") {
      return "Yes";
    } else if (value === false || value === "false" || value === "False") {
      return "No";
    } else {
      if (format != undefined && value != null) {
        var isCurrencyFormat: boolean = format == "{0:C}";
        var isDateFormat: boolean = format.includes("MM/dd/yyyy") || format.includes("DD/mm/yyyy") || format.includes("yyyy/dd/MM");
        var isNumberFormat: boolean = format == "{0:N2}";

        var browserLanguage = this.translateService.getBrowserCultureLang();

        if (isCurrencyFormat) {
          if (browserLanguage != undefined) {
            const currencyFormatter = new Intl.NumberFormat(browserLanguage, {
              style: 'currency',
              currency: getLocaleCurrencyCode(browserLanguage) ?? "USD"
            });

            value = currencyFormatter.format(value);
          }
        } else if (isDateFormat) {
          var options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
          };

          if (format.includes("hh")) {
            options.hour = "2-digit";
          }

          if (format.includes("mm")) {
            options.minute = "2-digit";
          }

          if (format.includes("ss")) {
            options.second = "2-digit";
          }

          var dateFormatter = new Intl.DateTimeFormat(browserLanguage, options);

          if (!value.toString().includes('Z')) {
            value = value + 'Z';
          }

          var date = new Date(value);
          var offset = date.getTimezoneOffset();
          value = dateFormatter.format(date);

        } else if (isNumberFormat) {
          var numberFormatter = new Intl.NumberFormat(browserLanguage, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });

          value = numberFormatter.format(value);
        } else {
          value = value;
        }
      }

      return value;
    }
  }

  trackByColumn(index: number, item: UiColumn): string {
    return item.field;
  }

  editBtnClick(rowIndex: number) {
    var e = window.event;
    e?.preventDefault();

    let x = window.scrollX;
    let y = window.scrollY;

    window.onscroll = () => {
      window.scrollTo(x, y);
    }

    setTimeout(() => {
      window.onscroll = () => { };
    });

    var htmlTable = this.table.nativeElement as HTMLTableElement;
    var dataRow = this.getDataRow(e, htmlTable); // table

    var title: string | undefined = this.tab.tabOptions.titleReplacement;
    if (!title) {
      title = this.tab.tabOptions.apiController.replace(/([A-Z])/g, ' $1');
    }

    var width: number = 800;
    var height: number = 500;

    if (this.tab.tabOptions.editWindowSettings != undefined) {
      if (this.tab.tabOptions.editWindowSettings.width != undefined) width = this.tab.tabOptions.editWindowSettings.width;
      if (this.tab.tabOptions.editWindowSettings.height != undefined) height = this.tab.tabOptions.editWindowSettings.height;
    }

    var options: WindowSettings = {
      title: 'Edit ' + title,
      width: width,
      height: height,
      content: EntityComponent[this.tab.tabOptions.apiController],
      draggable: true,
      resizable: true,
      top: (e as PointerEvent).pageY - 200
    };

    const win = this.windowService.open(options);

    const componentData = win.content.instance;
    componentData.entity = dataRow;

    win.result.subscribe((result) => {
      console.log(result);
    });

    win.content.instance.submitForm.subscribe((formData: FormGroup) => {
      // Save entity
      var entity = formData.value;

      this.detailService.save(entity, this.tab.tabOptions.apiController, entity['id'], false).subscribe((response) => {
        if (response.wasSuccessful) {
          this.feedback.give({ text: "Saved Successfully!", status: "success" });

          this.updateRow(response.entity, htmlTable, rowIndex);
          this.updateEntity(response.entity);
        }
        else {
          this.feedback.give({ text: response.message ?? "Error!", status: "error" });
        }

        win.close();
      });
    });
  }

  getDataRow(event: Event | undefined, table: HTMLTableElement): any {

    var dataItem: any = null;

    var button = event?.target as HTMLButtonElement;
    if (button) {
      var tableRow = button.closest("tr") as HTMLTableRowElement;
      if (tableRow) {
        var rows = table.rows;
        var index = this.indexOf(rows, tableRow, true);
        dataItem = this.data[index];
      }
    }

    return dataItem;
  }

  deleteBtnClick(rowIndex: number) {
    var e = window.event;
    e?.preventDefault();

    var options: DialogSettings = {
      title: "Please Confirm",
      animation: true,
      content: "Are you sure you want to delete this record?",
      actions: [{ text: "No" }, { text: "Yes", themeColor: "primary" }],
      actionsLayout: "end",
      width: 325,
      height: 150
    };

    const dialog: DialogRef = this.dialogService.open(options);

    dialog.result.subscribe((result) => {
      var text: string = Reflect.get(result, "text");
      if (text == "No" || result instanceof DialogCloseResult) {
        // do nothing
      } else {
        var dataRow = this.data[rowIndex];

        this.detailService.remove(dataRow['id'], this.tab.tabOptions.apiController).subscribe((response) => {
          if (response.wasSuccessful) {
            this.feedback.give({ text: "Removed Successfully!", status: "success" });
            this.data.splice(rowIndex, 1);
            this.dataChange.emit(this.data);
          } else {
            this.feedback.give({ text: response.message ?? "Error!", status: "error" });
          }
        });
      }
    });
  }

  indexOf<T>(source: HTMLCollectionOf<Element>, element: T, lookforHeader: boolean): number {
    var index = -1;
    for (var i = 0; i < source.length; i++) {
      if (lookforHeader && i == 0) {
        continue; // header row
      }
      var elm = source[i];
      if (elm == element) {
        index = (i - 1);
        break;
      }
    }
    return index;
  }

  addBtnClick(): void {
    var e = window.event;
    e?.preventDefault();

    let x = window.scrollX;
    let y = window.scrollY;

    window.onscroll = () => {
      window.scrollTo(x, y);
    }

    setTimeout(() => {
      window.onscroll = () => { };
    });

    var table: HTMLTableElement = document.getElementById(this.tab.label + "_table") as HTMLTableElement;

    var dataRow = this.getNewDataRow();

    var title: string | undefined = this.tab.tabOptions.titleReplacement;
    if (!title) {
      title = this.tab.tabOptions.apiController.replace(/([A-Z])/g, ' $1');
    }

    var width: number = 800;
    var height: number = 500;

    if (this.tab.tabOptions.editWindowSettings != undefined) {
      if (this.tab.tabOptions.editWindowSettings.width != undefined) width = this.tab.tabOptions.editWindowSettings.width;
      if (this.tab.tabOptions.editWindowSettings.height != undefined) height = this.tab.tabOptions.editWindowSettings.height;
    }

    var options: WindowSettings = {
      title: 'Add ' + title,
      width: width,
      height: height,
      content: EntityComponent[this.tab.tabOptions.apiController],
      draggable: true,
      resizable: true,
      top: (e as PointerEvent).pageY - 200
    };

    const win = this.windowService.open(options);

    const componentData = win.content.instance;
    componentData.entity = dataRow;

    win.result.subscribe((result) => {
      console.log(result);
    });

    win.content.instance.submitForm.subscribe((formData: FormGroup) => {
      // Save new entity
      var entity: any;
      if (!this.tab.tabOptions.postAsFormData) {
        entity = formData.value;
      } else {
        entity = formData;
      }

      var id = entity['id'];

      this.detailService.save(entity, this.tab.tabOptions.apiController, id ?? 0, false).subscribe((response) => {
        if (response.wasSuccessful) {
          this.feedback.give({ text: "Saved Successfully!", status: "success" });
          this.notificationService.show({ title: "Success!", type: "success", description: response.message ?? "The record has been added successfully!" });

          var dbEntity = response.entity;
          this.data.push(dbEntity);

          var nextIndex = table.rows.length - 1;
          //this.renderRow(dbEntity, table, nextIndex);
        }
        else {
          this.feedback.give({ text: response.message ?? "Error!", status: "error" });
          this.notificationService.show({ title: "Error!", type: "error", description: response.message ?? "Something Went Wrong!" });
        }

        win.close();
      });
    });
  }

  getNewDataRow(): any {

    var dataItem = this.data[0];

    type d = { [key: string]: any };
    var newDataItem: d = {};

    for (var key in dataItem) {

      if (this.tab.tabOptions.defaultValue != undefined) {

        var defaultValues = this.tab.tabOptions.defaultValue.values;
        var prop = defaultValues.filter(d => d.field == key)[0];

        if (prop) {
          newDataItem[key] = prop.value;
        } else {
          newDataItem[key] = null;
        }
      } else {
        newDataItem[key] = null;
      }
    }

    if (this.tab.tabOptions.defaultValue != undefined) {
      var keyfieldName = this.tab.tabOptions.defaultValue.keyFieldName;
      var foreignKey = this.tab.tabOptions.defaultValue.foreignKeyFieldName;
      try {
        var entity = this.entityService.get(this.entityService.getKey(this.tab.tabOptions.apiController, this.tab.parentEntityId));
        if ((entity == null || entity == undefined) && this.tab.tabOptions.defaultValue.apiController != undefined) {
          entity = this.entityService.get(this.entityService.getKey(this.tab.tabOptions.defaultValue.apiController, this.tab.parentEntityId));
        }
        newDataItem[foreignKey] = entity[keyfieldName];
        for (var key in newDataItem) {
          if (this.tab.tabOptions.defaultValue != undefined) {
            var values = this.tab.tabOptions.defaultValue.values;
            var prop = values.filter(d => d.field == key)[0];
            if (prop) {
              var mapping: string = prop.mapping ? prop.mapping : key;
              newDataItem[key] = entity[mapping];
            }
          }
        }
      } catch { }
    }

    if (this.tab.parentEntityId != undefined) {
      var parentEntityId = this.tab.parentEntityId;

      newDataItem["parentEntityId"] = parentEntityId;
    }

    return newDataItem;
  }

  updateEntity(entity: any): void {
    if (entity) {
      var dataRow = this.data.find(d => d['id'] == entity['id']);
      if (dataRow) {
        for (var key in entity) {
          dataRow[key] = entity[key];
        }
      }
    }
  }

  updateRow(entity: any, table: HTMLTableElement, rowIndex: number): void {
    if (table) {
      var tableRow = table.rows[rowIndex + 1];
      if (tableRow) {
        var cells = tableRow.cells;
        var columns = this.tab.tabOptions.columns;
        for (var i = 0; i < cells.length; i++) {
          var cell = cells[i];
          var currentIndex = this.indexOf(cells, cell, false);
          if (currentIndex == (cells.length - 1)) {
            continue;
          }

          var column = columns[i];
          if (column) {
            var value = this.getConvertedItemValue(entity[column.field], column.format);
            cell.textContent = value;
            cell.title = value;
          }
        }
      }
    }
  }

  callCustomBtnFunction(item: ToolbarItem, entity: any): void {
    if (item && item.action) {
      item.action(entity);
    }
  }

  callToolbarBtnFunction(item: ToolbarItem): void {
    if (item && item.action) {
      var e = window.event;
      e?.preventDefault();

      item.action();
    }
  }
}

declare global {
  interface Array<T> {
    skip(size: number): T[];
    take(size: number): T[];
    chunk(size: number): T[];
  }
}

Array.prototype.skip = function (size: number) {

  var tempArray = this;
  var remaining = tempArray.length - size;
  tempArray = tempArray.slice(size, remaining);

  return tempArray;
}

Array.prototype.take = function (size: number) {

  var tempArray = this;
  tempArray = tempArray.slice(0, size);

  return tempArray;
}

Array.prototype.chunk = function (size: number) {

  var arr = [];
  for (var i = 0; i < this.length; i += size) {
    arr.push(this.slice(i, i + size));
  }

  return arr;

}
